import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { TreePine, Users, Award, Clock, Calendar, Eye } from 'lucide-react';

interface StatsData {
  totalTrees: number;
  pendingTrees: number;
  approvedTrees: number;
  rejectedTrees: number;
  totalVotes: number;
  totalViews: number;
  averageVotes: number;
  participantsByHour: { hour: string; count: number }[];
  participantsByDay: { date: string; count: number }[];
  participantsByWeek: { week: string; count: number }[];
  participantsByMonth: { month: string; count: number }[];
  statusDistribution: { name: string; value: number }[];
}

const COLORS = ['#5D3E98', '#FF4E50', '#4CAF50', '#FFC107'];

const TIME_PERIODS = [
  { id: 'hour', label: 'Por Hora' },
  { id: 'day', label: 'Por Día' },
  { id: 'week', label: 'Por Semana' },
  { id: 'month', label: 'Por Mes' }
] as const;

type TimePeriod = typeof TIME_PERIODS[number]['id'];

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name, value }: any) => {
  if (value === 0) return null;

  const RADIAN = Math.PI / 180;
  const radius = outerRadius * 1.2;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const showPercentage = percent > 0.01;

  return (
    <text
      x={x}
      y={y}
      fill="#374151"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize="12"
    >
      {`${name} ${showPercentage ? `(${(percent * 100).toFixed(0)}%)` : ''}`}
    </text>
  );
};

export default function AdminStatistics() {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('day');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const treesRef = collection(db, 'trees');
        const snapshot = await getDocs(treesRef);
        const trees = snapshot.docs.map(doc => ({
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate()
        }));

        const totalTrees = trees.length;
        const pendingTrees = trees.filter(tree => tree.status === 'pending').length;
        const approvedTrees = trees.filter(tree => tree.status === 'approved').length;
        const rejectedTrees = trees.filter(tree => tree.status === 'rejected').length;

        const totalVotes = trees.reduce((sum, tree) => sum + (tree.votes || 0), 0);
        const totalViews = trees.reduce((sum, tree) => sum + (tree.views || 0), 0);
        const averageVotes = totalTrees > 0 ? Math.round(totalVotes / totalTrees) : 0;

        const formatDate = (date: Date) => {
          return new Intl.DateTimeFormat('es', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).format(date);
        };

        const getWeekNumber = (date: Date) => {
          const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
          const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
          return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
        };

        const participantsByHour = trees.reduce((acc: any[], tree) => {
          if (tree.createdAt) {
            const hour = tree.createdAt.getHours();
            const hourStr = `${hour.toString().padStart(2, '0')}:00`;
            const existing = acc.find(item => item.hour === hourStr);
            if (existing) {
              existing.count++;
            } else {
              acc.push({ hour: hourStr, count: 1 });
            }
          }
          return acc;
        }, Array.from({ length: 24 }, (_, i) => ({ 
          hour: `${i.toString().padStart(2, '0')}:00`, 
          count: 0 
        }))).sort((a, b) => a.hour.localeCompare(b.hour));

        const participantsByDay = trees.reduce((acc: any[], tree) => {
          if (tree.createdAt) {
            const date = formatDate(tree.createdAt);
            const existing = acc.find(item => item.date === date);
            if (existing) {
              existing.count++;
            } else {
              acc.push({ date, count: 1 });
            }
          }
          return acc;
        }, []).sort((a, b) => a.date.localeCompare(b.date));

        const participantsByWeek = trees.reduce((acc: any[], tree) => {
          if (tree.createdAt) {
            const week = `Semana ${getWeekNumber(tree.createdAt)}`;
            const existing = acc.find(item => item.week === week);
            if (existing) {
              existing.count++;
            } else {
              acc.push({ week, count: 1 });
            }
          }
          return acc;
        }, []).sort((a, b) => a.week.localeCompare(b.week));

        const participantsByMonth = trees.reduce((acc: any[], tree) => {
          if (tree.createdAt) {
            const month = new Intl.DateTimeFormat('es', { month: 'long', year: 'numeric' }).format(tree.createdAt);
            const existing = acc.find(item => item.month === month);
            if (existing) {
              existing.count++;
            } else {
              acc.push({ month, count: 1 });
            }
          }
          return acc;
        }, []).sort((a, b) => {
          const [aMonth, aYear] = a.month.split(' ');
          const [bMonth, bYear] = b.month.split(' ');
          return aYear === bYear ? 
            new Date(Date.parse(`${aMonth} 1, ${aYear}`)).getMonth() - 
            new Date(Date.parse(`${bMonth} 1, ${bYear}`)).getMonth() :
            parseInt(aYear) - parseInt(bYear);
        });

        const statusDistribution = [
          { name: 'Pendientes', value: pendingTrees },
          { name: 'Aprobados', value: approvedTrees },
          { name: 'Rechazados', value: rejectedTrees }
        ].filter(item => item.value > 0);

        setStats({
          totalTrees,
          pendingTrees,
          approvedTrees,
          rejectedTrees,
          totalVotes,
          totalViews,
          averageVotes,
          participantsByHour,
          participantsByDay,
          participantsByWeek,
          participantsByMonth,
          statusDistribution
        });
      } catch (error) {
        console.error('Error fetching statistics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!stats) return null;

  const StatCard = ({ icon: Icon, title, value, description }: any) => (
    <div className="bg-white p-6 rounded-xl shadow-lg">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <h3 className="text-2xl font-bold text-primary mb-1">{value}</h3>
          <p className="text-xs text-gray-500">{description}</p>
        </div>
        <Icon className="h-8 w-8 text-primary opacity-20" />
      </div>
    </div>
  );

  const getChartData = () => {
    switch (selectedPeriod) {
      case 'hour':
        return {
          data: stats.participantsByHour,
          dataKey: 'hour',
          label: 'Participaciones por Hora'
        };
      case 'day':
        return {
          data: stats.participantsByDay,
          dataKey: 'date',
          label: 'Participaciones por Día'
        };
      case 'week':
        return {
          data: stats.participantsByWeek,
          dataKey: 'week',
          label: 'Participaciones por Semana'
        };
      case 'month':
        return {
          data: stats.participantsByMonth,
          dataKey: 'month',
          label: 'Participaciones por Mes'
        };
    };
  };

  const chartData = getChartData();

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <StatCard
          icon={TreePine}
          title="Total de Árboles"
          value={stats.totalTrees}
          description="Participaciones totales"
        />
        <StatCard
          icon={Clock}
          title="Pendientes"
          value={stats.pendingTrees}
          description="Necesitan revisión"
        />
        <StatCard
          icon={Award}
          title="Aprobados"
          value={stats.approvedTrees}
          description="Árboles publicados"
        />
        <StatCard
          icon={Users}
          title="Votos Totales"
          value={stats.totalVotes}
          description={`Promedio: ${stats.averageVotes} por árbol`}
        />
        <StatCard
          icon={Eye}
          title="Visualizaciones"
          value={stats.totalViews}
          description="Total de vistas"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Participaciones Chart */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4 sm:mb-0">
              {chartData.label}
            </h3>
            <div className="flex flex-wrap gap-2">
              {TIME_PERIODS.map((period) => (
                <button
                  key={period.id}
                  onClick={() => setSelectedPeriod(period.id)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    selectedPeriod === period.id
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {period.label}
                </button>
              ))}
            </div>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              {selectedPeriod === 'hour' ? (
                <LineChart data={chartData.data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey={chartData.dataKey} />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#5D3E98" />
                </LineChart>
              ) : (
                <BarChart data={chartData.data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey={chartData.dataKey} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#5D3E98" />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>

        {/* Distribución por Estado */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h3 className="text-lg font-bold text-gray-800 mb-6">Distribución por Estado</h3>
          <div className="h-[300px] flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stats.statusDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={renderCustomizedLabel}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  minAngle={5}
                  startAngle={90}
                  endAngle={-270}
                >
                  {stats.statusDistribution.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={COLORS[index % COLORS.length]}
                      strokeWidth={entry.value === 0 ? 0 : 1}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          {/* Legend */}
          <div className="flex justify-center mt-4 flex-wrap gap-4">
            {stats.statusDistribution.map((entry, index) => (
              <div key={entry.name} className="flex items-center">
                <div 
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="text-sm text-gray-600">
                  {entry.name}: {entry.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}